import React, { useState, useEffect } from 'react';
import {
  Home,
  MapPin,
  Heart,
  Mail,
  Phone,
  Clock,
  Users,
  BedDouble,
  MessageCircle
} from 'lucide-react';

const translations = {
  es: {
    nav: {
      home: 'Inicio',
      apartments: 'Apartamentos',
      contact: 'Contacto',
      book: 'Reservar'
    },
    hero: {
      title: 'Vive Triana como un local',
      subtitle: 'Apartamentos turísticos con encanto en el corazón de Sevilla',
      description: 'Descubre la esencia más auténtica de la ciudad alojándote en apartamentos turísticos en Triana, el barrio más tradicional, creativo y lleno de vida de Sevilla. Espacios cuidadosamente diseñados para que te sientas como en casa, ya sea por ocio, trabajo o una escapada en pareja.',
      cta: 'Reserva directo. Mejor precio garantizado.'
    },
    why: {
      title: '¿Por qué alojarte con nosotros?',
      feature1: {
        title: 'Apartamentos totalmente equipados',
        desc: 'Cocina completa, Wi-Fi rápido, aire acondicionado y camas cómodas para un descanso perfecto.'
      },
      feature2: {
        title: 'Ubicación privilegiada en Triana',
        desc: 'A pasos del río Guadalquivir, la Calle Betis y el Puente de Triana.'
      },
      feature3: {
        title: 'Diseño y comodidad',
        desc: 'Estilo andaluz moderno, luminosos y pensados para estancias cortas o medias.'
      },
      feature4: {
        title: 'Atención cercana y personalizada',
        desc: 'Recomendaciones locales y soporte durante toda tu estancia.'
      }
    },
    ourApartments: {
      title: 'Nuestros apartamentos',
      subtitle: 'Desde estudios acogedores hasta apartamentos familiares, todos cuentan con:',
      amenities: [
        'Check-in sencillo y flexible',
        'Limpieza profesional',
        'Ropa de cama y toallas incluidas',
        'Espacios tranquilos para descansar después de recorrer la ciudad'
      ]
    },
    triana: {
      title: 'Triana: tradición, sabor y alma sevillana',
      description: 'Triana es flamenco, cerámica, tapas y atardeceres junto al río. Un barrio con identidad propia, bien conectado con el centro histórico y perfecto para vivir Sevilla sin prisas.'
    },
    bookNow: {
      title: 'Reserva tu estancia hoy',
      description: 'Vive Sevilla desde su barrio más auténtico. Consulta disponibilidad y asegura tu apartamento en Triana al mejor precio.'
    },
    apartmentsPage: {
      title: 'Apartamentos',
      subtitle: 'Tu alojamiento ideal en Triana, Sevilla',
      intro: 'Nuestros apartamentos turísticos en Triana están pensados para que disfrutes de una estancia cómoda, tranquila y con todo lo que necesitas, ya vengas por unos días o por una temporada más larga. Espacios cuidados al detalle, bien ubicados y con el encanto propio del barrio.',
      typesTitle: 'Tipos de apartamentos',
      studio: {
        title: 'Estudios',
        desc: 'Ideales para parejas o viajeros solos. Espacios funcionales, luminosos y perfectamente equipados para una estancia práctica y acogedora.'
      },
      oneBed: {
        title: 'Apartamentos de 1 dormitorio',
        desc: 'Perfectos para escapadas en pareja o viajes de trabajo. Más amplitud, zonas diferenciadas y total privacidad.'
      },
      family: {
        title: 'Apartamentos familiares',
        desc: 'Pensados para familias o grupos pequeños. Cómodos, silenciosos y con espacio suficiente para sentirse como en casa.'
      },
      includesTitle: '¿Qué incluyen nuestros apartamentos?',
      includes: [
        'Cocina totalmente equipada',
        'Wi-Fi de alta velocidad',
        'Aire acondicionado y calefacción',
        'Ropa de cama y toallas',
        'Televisión y zona de descanso',
        'Limpieza profesional',
        'Check-in fácil y flexible'
      ],
      design: {
        title: 'Diseño con esencia sevillana',
        desc: 'Cada apartamento combina estilo andaluz y comodidad moderna, creando un ambiente cálido y relajante para desconectar después de recorrer la ciudad.'
      },
      location: {
        title: 'Ubicación inmejorable',
        desc: 'Situados en el corazón de Triana, podrás disfrutar de bares de tapas, comercios locales y paseos junto al río, con el centro histórico a pocos minutos caminando.'
      },
      bookCta: {
        title: 'Reserva con total confianza',
        desc: 'Consulta disponibilidad, elige tu apartamento ideal y asegura el mejor precio reservando directamente con nosotros.',
        cta: 'Tu estancia en Triana empieza aquí.'
      }
    },
    contactPage: {
      title: 'Contacto',
      subtitle: 'Estamos aquí para ayudarte',
      intro: '¿Tienes dudas sobre tu reserva, disponibilidad o necesitas una recomendación para tu estancia en Triana? Escríbenos y te responderemos lo antes posible. Nos encanta ayudarte a vivir una experiencia auténtica en Sevilla.',
      formTitle: 'Escríbenos',
      formSubtitle: 'Completa el formulario y nos pondremos en contacto contigo:',
      form: {
        name: 'Nombre y apellidos',
        email: 'Correo electrónico',
        phone: 'Teléfono (opcional)',
        dates: 'Fechas de estancia',
        message: 'Mensaje',
        submit: 'Enviar mensaje'
      },
      response: 'Respondemos en menos de 24 horas.',
      locationTitle: 'Ubicación',
      locationDesc: 'Nuestros apartamentos están ubicados en el barrio de Triana, una de las zonas mejor conectadas y con más encanto de Sevilla.',
      channelsTitle: 'Otros canales de contacto',
      channels: {
        email: 'Email: info@trianamiaapartments.com',
        phone: 'Teléfono / WhatsApp: +34 600 000 000',
        hours: 'Horario de atención: Lunes a domingo, 9:00 – 21:00'
      },
      personalTitle: 'Atención cercana y personalizada',
      personalDesc: 'Te acompañamos antes, durante y después de tu estancia para que solo te preocupes de disfrutar Sevilla como un local.',
      finalCta: 'Contáctanos hoy y empieza a planear tu escapada a Triana.'
    },
    footer: {
      about: 'Sobre nosotros',
      aboutText: 'Apartamentos turísticos con encanto en el corazón de Triana, Sevilla.',
      links: 'Enlaces rápidos',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.',
      funding: 'Financiado por la Unión Europea – NextGenerationEu'
    }
  },
  en: {
    nav: {
      home: 'Home',
      apartments: 'Apartments',
      contact: 'Contact',
      book: 'Book Now'
    },
    hero: {
      title: 'Live Triana Like a Local',
      subtitle: 'Charming Tourist Apartments in the Heart of Seville',
      description: 'Discover the most authentic essence of the city by staying in tourist apartments in Triana, the most traditional, creative and lively neighborhood in Seville. Carefully designed spaces to make you feel at home, whether for leisure, work or a romantic getaway.',
      cta: 'Book direct. Best price guaranteed.'
    },
    why: {
      title: 'Why stay with us?',
      feature1: {
        title: 'Fully Equipped Apartments',
        desc: 'Complete kitchen, fast Wi-Fi, air conditioning and comfortable beds for a perfect rest.'
      },
      feature2: {
        title: 'Prime Location in Triana',
        desc: 'Steps away from the Guadalquivir River, Calle Betis and the Triana Bridge.'
      },
      feature3: {
        title: 'Design and Comfort',
        desc: 'Modern Andalusian style, bright and designed for short or medium stays.'
      },
      feature4: {
        title: 'Close and Personalized Service',
        desc: 'Local recommendations and support throughout your stay.'
      }
    },
    ourApartments: {
      title: 'Our apartments',
      subtitle: 'From cozy studios to family apartments, all feature:',
      amenities: [
        'Simple and flexible check-in',
        'Professional cleaning',
        'Bed linen and towels included',
        'Quiet spaces to rest after exploring the city'
      ]
    },
    triana: {
      title: 'Triana: tradition, flavor and Sevillian soul',
      description: 'Triana is flamenco, ceramics, tapas and sunsets by the river. A neighborhood with its own identity, well connected to the historic center and perfect for experiencing Seville without rush.'
    },
    bookNow: {
      title: 'Book your stay today',
      description: 'Experience Seville from its most authentic neighborhood. Check availability and secure your apartment in Triana at the best price.'
    },
    apartmentsPage: {
      title: 'Apartments',
      subtitle: 'Your Ideal Accommodation in Triana, Seville',
      intro: 'Our tourist apartments in Triana are designed for you to enjoy a comfortable, quiet stay with everything you need, whether you come for a few days or a longer season. Carefully detailed spaces, well located and with the charm of the neighborhood.',
      typesTitle: 'Types of apartments',
      studio: {
        title: 'Studios',
        desc: 'Ideal for couples or solo travelers. Functional, bright spaces perfectly equipped for a practical and cozy stay.'
      },
      oneBed: {
        title: '1 Bedroom Apartments',
        desc: 'Perfect for romantic getaways or business trips. More spacious, differentiated areas and total privacy.'
      },
      family: {
        title: 'Family Apartments',
        desc: 'Designed for families or small groups. Comfortable, quiet and with enough space to feel at home.'
      },
      includesTitle: 'What do our apartments include?',
      includes: [
        'Fully equipped kitchen',
        'High-speed Wi-Fi',
        'Air conditioning and heating',
        'Bed linen and towels',
        'Television and rest area',
        'Professional cleaning',
        'Easy and flexible check-in'
      ],
      design: {
        title: 'Design with Sevillian Essence',
        desc: 'Each apartment combines Andalusian style and modern comfort, creating a warm and relaxing atmosphere to unwind after exploring the city.'
      },
      location: {
        title: 'Unbeatable Location',
        desc: 'Located in the heart of Triana, you can enjoy tapas bars, local shops and riverside walks, with the historic center just minutes away on foot.'
      },
      bookCta: {
        title: 'Book with Total Confidence',
        desc: 'Check availability, choose your ideal apartment and secure the best price by booking directly with us.',
        cta: 'Your stay in Triana starts here.'
      }
    },
    contactPage: {
      title: 'Contact',
      subtitle: 'We are here to help you',
      intro: 'Do you have questions about your reservation, availability or need a recommendation for your stay in Triana? Write to us and we will respond as soon as possible. We love helping you live an authentic experience in Seville.',
      formTitle: 'Write to us',
      formSubtitle: 'Complete the form and we will contact you:',
      form: {
        name: 'Full name',
        email: 'Email address',
        phone: 'Phone (optional)',
        dates: 'Stay dates',
        message: 'Message',
        submit: 'Send message'
      },
      response: 'We respond in less than 24 hours.',
      locationTitle: 'Location',
      locationDesc: 'Our apartments are located in the Triana neighborhood, one of the best connected and most charming areas of Seville.',
      channelsTitle: 'Other contact channels',
      channels: {
        email: 'Email: info@trianamiaapartments.com',
        phone: 'Phone / WhatsApp: +34 600 000 000',
        hours: 'Service hours: Monday to Sunday, 9:00 AM – 9:00 PM'
      },
      personalTitle: 'Close and Personalized Service',
      personalDesc: 'We accompany you before, during and after your stay so you only worry about enjoying Seville like a local.',
      finalCta: 'Contact us today and start planning your escape to Triana.'
    },
    footer: {
      about: 'About us',
      aboutText: 'Charming tourist apartments in the heart of Triana, Seville.',
      links: 'Quick links',
      contact: 'Contact',
      rights: 'All rights reserved.',
      funding: 'Funded by the European Union – NextGenerationEu'
    }
  }
};

function App() {
  const [language, setLanguage] = useState('es');
  const [currentPage, setCurrentPage] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    message: ''
  });

  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = language === 'es'
      ? 'Triana Mía Apartments — Apartamentos turísticos en Triana, Sevilla'
      : 'Triana Mía Apartments — Tourist apartments in Triana, Seville';
  }, [language]);

  const navigateTo = (page) => {
    setCurrentPage(page);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(language === 'es' ? '¡Gracias! Te contactaremos pronto.' : 'Thank you! We will contact you soon.');
  };

  return (
    <div className="app">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Cormorant+Garamond:wght@300;400;500;600&family=Lora:wght@400;500;600&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --color-cream: #F5F0E8;
          --color-beige: #E8DDD0;
          --color-tan: #D4C5B0;
          --color-brown: #75614C;
          --color-dark-brown: #6B5744;
          --color-gold: #806038;
          --color-gold-light: #F2D0A0;
          --color-terracotta: #D4956C;
          --color-olive: #8B8B6E;
          --color-white: #FFFFFF;
          --color-text: #3D3329;
          --font-display: 'Playfair Display', serif;
          --font-body: 'Cormorant Garamond', serif;
          --font-accent: 'Lora', serif;
        }

        .skip-link {
          position: absolute;
          top: -100px;
          left: 0;
          background: var(--color-dark-brown);
          color: var(--color-white);
          padding: 0.85rem 1.5rem;
          z-index: 9999;
          text-decoration: none;
          font-family: var(--font-accent);
          font-size: 1rem;
          font-weight: 500;
          letter-spacing: 0.05em;
          border-radius: 0 0 6px 0;
          transition: top 0.2s ease;
        }

        .skip-link:focus {
          top: 0;
          outline: 3px solid var(--color-gold);
          outline-offset: 2px;
        }

        button:focus-visible,
        a:focus-visible,
        input:focus-visible,
        textarea:focus-visible {
          outline: 3px solid var(--color-gold);
          outline-offset: 2px;
        }

        body {
          font-family: var(--font-body);
          color: var(--color-text);
          background: var(--color-cream);
          line-height: 1.7;
          overflow-x: hidden;
        }

        .app {
          position: relative;
        }

        /* Header Styles */
        header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.4s ease;
          background: var(--color-cream);
          box-shadow: 0 2px 10px rgba(61, 51, 41, 0.05);
        }

        header.scrolled {
          background: var(--color-cream);
          box-shadow: 0 4px 20px rgba(61, 51, 41, 0.12);
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1.5rem 3rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-decoration: none;
          transition: transform 0.3s ease;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          font-family: inherit;
        }

        .logo:hover {
          transform: translateY(-2px);
        }

        .logo-ornament {
          width: 80px;
          height: 20px;
          margin-bottom: 0.5rem;
        }

        .logo-text {
          font-family: var(--font-display);
          font-size: 1.8rem;
          font-weight: 600;
          letter-spacing: 0.15em;
          color: var(--color-brown);
          text-transform: uppercase;
        }

        .logo-subtitle {
          font-family: var(--font-accent);
          font-size: 0.75rem;
          letter-spacing: 0.3em;
          color: var(--color-dark-brown);
          margin-top: -0.25rem;
        }

        nav {
          display: flex;
          gap: 2.5rem;
          align-items: center;
        }

        nav button {
          background: none;
          border: none;
          font-family: var(--font-accent);
          font-size: 1rem;
          color: var(--color-dark-brown);
          cursor: pointer;
          position: relative;
          padding: 0.5rem 0;
          transition: color 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 500;
        }

        nav button::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: var(--color-gold);
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }

        nav button:hover::after,
        nav button.active::after {
          width: 100%;
        }

        nav button:hover {
          color: var(--color-dark-brown);
        }

        .language-toggle {
          display: flex;
          gap: 0.5rem;
          margin-left: 1rem;
          padding-left: 1.5rem;
          border-left: 1px solid var(--color-tan);
        }

        .language-toggle button {
          padding: 0.4rem 0.8rem;
          background: transparent;
          border: 1px solid var(--color-tan);
          border-radius: 4px;
          font-family: var(--font-accent);
          font-size: 0.85rem;
          color: var(--color-dark-brown);
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .language-toggle button.active {
          background: var(--color-brown);
          color: var(--color-white);
          border-color: var(--color-brown);
        }

        .language-toggle button:hover {
          background: var(--color-beige);
        }

        /* Hero Section */
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          background: var(--color-cream);
          overflow: hidden;
          padding: 8rem 3rem 4rem;
        }

        .hero-grid {
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        .hero-content {
          padding: 1rem;
          position: relative;
          z-index: 1;
          animation: fadeInUp 1.2s ease;
          background: var(--color-cream);
          text-align: left;
        }

        .hero-image {
          width: 100%;
          height: 500px;
          border-radius: 12px;
          object-fit: cover;
          box-shadow: 0 12px 40px rgba(61, 51, 41, 0.18);
          animation: fadeIn 1.4s ease 0.2s both;
        }

        .hero .ornament {
          margin: 0 0 2rem 0;
        }

        .hero h1,
        .hero h2,
        .hero p {
          margin-left: 0;
          margin-right: 0;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ornament {
          width: 120px;
          height: 40px;
          margin: 0 auto 2rem;
          opacity: 0;
          animation: fadeIn 1s ease 0.3s forwards;
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        .hero h1 {
          font-family: var(--font-display);
          font-size: 4rem;
          font-weight: 600;
          color: var(--color-brown);
          margin-bottom: 1rem;
          letter-spacing: 0.02em;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .hero h2 {
          font-family: var(--font-accent);
          font-size: 1.5rem;
          color: var(--color-dark-brown);
          margin-bottom: 2rem;
          font-weight: 600;
          letter-spacing: 0.05em;
        }

        .hero p {
          font-size: 1.2rem;
          color: var(--color-text);
          margin-bottom: 2.5rem;
          line-height: 1.8;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-badge {
          display: inline-block;
          padding: 1rem 2.5rem;
          background: var(--color-brown);
          color: var(--color-white);
          font-family: var(--font-accent);
          font-size: 1.1rem;
          text-decoration: none;
          border-radius: 50px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(139, 115, 85, 0.3);
          letter-spacing: 0.05em;
          font-weight: 500;
          cursor: pointer;
          border: none;
        }

        .cta-badge:hover {
          background: var(--color-dark-brown);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 115, 85, 0.4);
        }

        /* Section Styles */
        section {
          padding: 6rem 3rem;
          position: relative;
        }

        .section-title {
          font-family: var(--font-display);
          font-size: 3rem;
          color: var(--color-brown);
          text-align: center;
          margin-bottom: 3rem;
          letter-spacing: 0.02em;
          position: relative;
        }

        .section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 3px;
          background: var(--color-gold);
          margin: 1.5rem auto 0;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 3rem;
          margin-top: 4rem;
        }

        .feature-card {
          background: var(--color-white);
          padding: 2.5rem;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          transition: all 0.4s ease;
          border: 1px solid var(--color-tan);
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--color-gold), var(--color-terracotta));
          transform: scaleX(0);
          transition: transform 0.4s ease;
        }

        .feature-card:hover::before {
          transform: scaleX(1);
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }

        .feature-icon {
          margin-bottom: 1.5rem;
          display: block;
        }

        .feature-card h3 {
          font-family: var(--font-display);
          font-size: 1.5rem;
          color: var(--color-brown);
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .feature-card p {
          color: var(--color-dark-brown);
          font-size: 1.1rem;
          line-height: 1.7;
        }

        /* Amenities List */
        .amenities-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .amenity-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--color-white);
          border-radius: 8px;
          border-left: 3px solid var(--color-gold);
          transition: all 0.3s ease;
        }

        .amenity-item:hover {
          transform: translateX(8px);
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }

        .amenity-item::before {
          content: '✓';
          color: var(--color-gold);
          font-weight: bold;
          font-size: 1.3rem;
        }

        /* Triana Section */
        .triana-section {
          background: var(--color-cream);
          position: relative;
          overflow: hidden;
        }

        .triana-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
          background: var(--color-cream);
        }

        .triana-content p {
          font-size: 1.3rem;
          line-height: 1.9;
          color: var(--color-text);
        }

        /* Page Header */
        .page-header {
          padding: 10rem 3rem 4rem;
          background: var(--color-cream);
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .page-header h1 {
          font-family: var(--font-display);
          font-size: 3.5rem;
          color: var(--color-dark-brown);
          margin-bottom: 1rem;
          position: relative;
          z-index: 1;
        }

        .page-header p {
          font-family: var(--font-accent);
          font-size: 1.3rem;
          color: var(--color-dark-brown);
          position: relative;
          z-index: 1;
        }

        /* Apartments Page */
        .apartments-intro {
          text-align: center;
          max-width: 900px;
          margin: 0 auto 4rem;
          font-size: 1.2rem;
          color: var(--color-dark-brown);
          line-height: 1.8;
        }

        .apartment-types {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
          margin: 4rem 0;
        }

        .apartment-card {
          background: var(--color-white);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 6px 25px rgba(0,0,0,0.08);
          transition: all 0.4s ease;
          border: 1px solid var(--color-tan);
        }

        .apartment-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 12px 35px rgba(0,0,0,0.15);
        }

        .apartment-image {
          width: 100%;
          height: 250px;
          background-size: cover;
          background-position: center;
          position: relative;
        }

        .apartment-image::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(to top, rgba(0,0,0,0.4), transparent);
        }

        .apartment-content {
          padding: 2rem;
        }

        .apartment-card h3 {
          font-family: var(--font-display);
          font-size: 1.8rem;
          color: var(--color-brown);
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .apartment-card p {
          color: var(--color-dark-brown);
          font-size: 1.1rem;
          line-height: 1.7;
        }

        .includes-section {
          background: var(--color-beige);
          padding: 4rem 2rem;
          border-radius: 12px;
          margin: 4rem 0;
        }

        .includes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .include-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.2rem;
          background: var(--color-white);
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .include-item:hover {
          transform: translateX(10px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .include-item::before {
          content: '◆';
          color: var(--color-gold);
          font-size: 1.2rem;
        }

        .info-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 3rem;
          margin: 4rem 0;
        }

        .info-card {
          background: var(--color-white);
          padding: 3rem;
          border-radius: 12px;
          box-shadow: 0 6px 25px rgba(0,0,0,0.08);
          border: 1px solid var(--color-tan);
          transition: all 0.4s ease;
        }

        .info-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 35px rgba(0,0,0,0.12);
        }

        .info-card h2 {
          font-family: var(--font-display);
          font-size: 1.8rem;
          color: var(--color-brown);
          margin-bottom: 1.5rem;
          font-weight: 600;
        }

        .info-card p {
          font-size: 1.1rem;
          color: var(--color-dark-brown);
          line-height: 1.8;
        }

        /* Contact Page */
        .contact-intro {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 4rem;
          font-size: 1.2rem;
          color: var(--color-dark-brown);
          line-height: 1.8;
        }

        .contact-form-section {
          background: var(--color-white);
          padding: 4rem;
          border-radius: 12px;
          box-shadow: 0 6px 30px rgba(0,0,0,0.08);
          max-width: 700px;
          margin: 0 auto 4rem;
          border: 1px solid var(--color-tan);
        }

        .contact-form-section h2 {
          font-family: var(--font-display);
          font-size: 2rem;
          color: var(--color-brown);
          margin-bottom: 1rem;
          text-align: center;
        }

        .form-subtitle {
          text-align: center;
          color: var(--color-dark-brown);
          margin-bottom: 2.5rem;
          font-size: 1.1rem;
        }

        .form-group {
          margin-bottom: 1.8rem;
        }

        .form-group label {
          display: block;
          font-family: var(--font-accent);
          font-weight: 500;
          color: var(--color-brown);
          margin-bottom: 0.6rem;
          font-size: 1rem;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 1rem;
          border: 2px solid var(--color-tan);
          border-radius: 8px;
          font-family: var(--font-body);
          font-size: 1rem;
          color: var(--color-text);
          transition: all 0.3s ease;
          background: var(--color-cream);
        }

        .form-group input:focus,
        .form-group textarea:focus {
          border-color: var(--color-gold);
          background: var(--color-white);
          box-shadow: 0 0 0 3px rgba(128, 96, 56, 0.25);
        }

        .form-group input:focus-visible,
        .form-group textarea:focus-visible {
          outline: 3px solid var(--color-gold);
          outline-offset: 2px;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #6B5744;
          opacity: 0.85;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 150px;
        }

        .submit-button {
          width: 100%;
          padding: 1.2rem;
          background: var(--color-brown);
          color: var(--color-white);
          border: none;
          border-radius: 50px;
          font-family: var(--font-accent);
          font-size: 1.1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .submit-button:hover {
          background: var(--color-dark-brown);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 115, 85, 0.4);
        }

        .response-time {
          text-align: center;
          color: var(--color-gold);
          margin-top: 1.5rem;
          font-style: italic;
          font-size: 1rem;
        }

        .contact-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 3rem;
          margin: 4rem 0;
        }

        .contact-info-card {
          background: var(--color-white);
          padding: 3rem;
          border-radius: 12px;
          box-shadow: 0 6px 25px rgba(0,0,0,0.08);
          border: 1px solid var(--color-tan);
        }

        .contact-info-card h2 {
          font-family: var(--font-display);
          font-size: 1.8rem;
          color: var(--color-brown);
          margin-bottom: 1.5rem;
          font-weight: 600;
        }

        .contact-info-card p {
          color: var(--color-dark-brown);
          margin-bottom: 1rem;
          font-size: 1.1rem;
          line-height: 1.7;
        }

        .contact-detail {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 0.8rem;
          background: var(--color-white);
          border-radius: 6px;
          border-left: 3px solid var(--color-gold);
        }

        .final-cta-section {
          background: var(--color-beige);
          padding: 4rem;
          border-radius: 12px;
          text-align: center;
          margin-top: 4rem;
        }

        .final-cta-section h2 {
          font-family: var(--font-display);
          font-size: 2.2rem;
          color: var(--color-dark-brown);
          margin-bottom: 1.5rem;
        }

        .final-cta-section p {
          font-size: 1.2rem;
          color: var(--color-text);
          margin-bottom: 2rem;
          line-height: 1.8;
        }

        /* Footer */
        footer {
          background: var(--color-dark-brown);
          color: var(--color-cream);
          padding: 4rem 3rem 2rem;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 3rem;
          margin-bottom: 2rem;
        }

        .footer-section h3 {
          font-family: var(--font-display);
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          color: var(--color-gold-light);
        }

        .footer-section p,
        .footer-section a {
          color: var(--color-cream);
          text-decoration: none;
          display: block;
          margin-bottom: 0.8rem;
          font-size: 1rem;
          transition: color 0.3s ease;
        }

        .footer-section a:hover {
          color: var(--color-gold-light);
          cursor: pointer;
        }

        .footer-link {
          background: none;
          border: none;
          color: var(--color-cream);
          text-decoration: none;
          display: block;
          margin-bottom: 0.8rem;
          font-size: 1rem;
          transition: color 0.3s ease;
          cursor: pointer;
          padding: 0;
          text-align: left;
          font-family: var(--font-body);
        }

        .footer-link:hover {
          color: var(--color-gold-light);
        }

        .footer-funding {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 0;
          border-top: 1px solid rgba(232, 221, 208, 0.2);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 2rem;
        }

        .footer-funding-text {
          font-size: 0.9rem;
          color: var(--color-beige);
        }

        .footer-funding-logos {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .footer-funding-logos img {
          height: 50px;
          object-fit: contain;
          background: white;
          padding: 0.5rem;
          border-radius: 4px;
        }

        .footer-bottom {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(232, 221, 208, 0.2);
          color: var(--color-beige);
          font-size: 0.95rem;
        }

        /* Decorative SVG Ornaments */
        .ornament-svg {
          fill: var(--color-gold);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 1.5rem;
            padding: 1.5rem;
          }

          nav {
            flex-direction: column;
            gap: 1rem;
          }

          .language-toggle {
            margin-left: 0;
            padding-left: 0;
            border-left: none;
            border-top: 1px solid var(--color-tan);
            padding-top: 1rem;
          }

          .hero {
            padding: 7rem 1.5rem 3rem;
          }

          .hero-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .hero-image {
            height: 280px;
            order: -1;
          }

          .hero-content {
            text-align: center;
          }

          .hero h1 {
            font-size: 2.5rem;
          }

          .hero h2 {
            font-size: 1.2rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .page-header h1 {
            font-size: 2.5rem;
          }

          section {
            padding: 4rem 1.5rem;
          }

          .features-grid,
          .apartment-types,
          .info-cards {
            grid-template-columns: 1fr;
          }

          .contact-form-section {
            padding: 2rem;
          }

          .footer-funding {
            flex-direction: column;
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .hero h1 {
            font-size: 2rem;
          }

          .logo-text {
            font-size: 1.4rem;
          }

          .section-title {
            font-size: 1.8rem;
          }

          .page-header {
            padding: 8rem 1.5rem 3rem;
          }

          .page-header h1 {
            font-size: 2rem;
          }
        }
      `}</style>

      {/* Skip link for keyboard / screen reader users */}
      <a href="#main-content" className="skip-link">
        {language === 'es' ? 'Saltar al contenido principal' : 'Skip to main content'}
      </a>

      {/* Header */}
      <header className={scrolled ? 'scrolled' : ''}>
        <div className="header-content">
          <button
            type="button"
            className="logo"
            onClick={() => navigateTo('home')}
            aria-label={language === 'es' ? 'Ir a la página de inicio' : 'Go to home page'}
          >
            <svg className="logo-ornament" viewBox="0 0 200 50" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path className="ornament-svg" d="M100,10 Q90,5 80,10 Q70,15 60,10 L55,15 Q65,20 75,15 Q85,10 95,15 L100,25 L105,15 Q115,10 125,15 Q135,20 145,15 L140,10 Q130,15 120,10 Q110,5 100,10 Z"/>
              <ellipse className="ornament-svg" cx="100" cy="8" rx="8" ry="12"/>
            </svg>
            <span className="logo-text">TRIANA MÍA</span>
            <span className="logo-subtitle">APARTMENTS</span>
          </button>
          
          <nav aria-label={language === 'es' ? 'Navegación principal' : 'Main navigation'}>
            <button
              className={currentPage === 'home' ? 'active' : ''}
              onClick={() => navigateTo('home')}
              aria-current={currentPage === 'home' ? 'page' : undefined}
            >
              {t.nav.home}
            </button>
            <button
              className={currentPage === 'apartments' ? 'active' : ''}
              onClick={() => navigateTo('apartments')}
              aria-current={currentPage === 'apartments' ? 'page' : undefined}
            >
              {t.nav.apartments}
            </button>
            <button
              className={currentPage === 'contact' ? 'active' : ''}
              onClick={() => navigateTo('contact')}
              aria-current={currentPage === 'contact' ? 'page' : undefined}
            >
              {t.nav.contact}
            </button>

            <div className="language-toggle" role="group" aria-label={language === 'es' ? 'Selector de idioma' : 'Language selector'}>
              <button
                className={language === 'es' ? 'active' : ''}
                onClick={() => setLanguage('es')}
                aria-pressed={language === 'es'}
                lang="es"
              >
                ES
              </button>
              <button
                className={language === 'en' ? 'active' : ''}
                onClick={() => setLanguage('en')}
                aria-pressed={language === 'en'}
                lang="en"
              >
                EN
              </button>
            </div>
          </nav>
        </div>
      </header>

      <main id="main-content" tabIndex="-1">
      {/* HOME PAGE */}
      {currentPage === 'home' && (
        <>
          {/* Hero Section */}
          <section className="hero" aria-labelledby="hero-title">
            <div className="hero-grid">
              <div className="hero-content">
                <svg className="ornament" viewBox="0 0 300 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path className="ornament-svg" d="M150,20 Q140,10 130,20 Q120,30 110,20 Q100,10 90,20 Q80,30 70,20 L60,30 Q70,40 80,30 Q90,20 100,30 Q110,40 120,30 Q130,20 140,30 Q145,35 150,50 Q155,35 160,30 Q170,20 180,30 Q190,40 200,30 Q210,20 220,30 Q230,40 240,30 L230,20 Q220,30 210,20 Q200,10 190,20 Q180,30 170,20 Q160,10 150,20 Z"/>
                  <ellipse className="ornament-svg" cx="150" cy="15" rx="12" ry="18"/>
                  <path className="ornament-svg" d="M30,50 Q40,60 50,50 M250,50 Q260,60 270,50"/>
                </svg>

                <h1 id="hero-title">{t.hero.title}</h1>
                <h2>{t.hero.subtitle}</h2>
                <p>{t.hero.description}</p>
                <button className="cta-badge" onClick={() => navigateTo('contact')}>
                  {t.hero.cta}
                </button>
              </div>
              <img
                className="hero-image"
                src="https://images.unsplash.com/photo-1688502700632-9690247e1914?w=1200&q=80&auto=format&fit=crop"
                alt={language === 'es' ? 'Vista del río Guadalquivir y el barrio de Triana en Sevilla' : 'View of the Guadalquivir river and the Triana neighborhood in Seville'}
                loading="eager"
              />
            </div>
          </section>

          {/* Why Stay Section */}
          <section>
            <div className="container">
              <h2 className="section-title">{t.why.title}</h2>
              <div className="features-grid">
                <div className="feature-card">
                  <Home className="feature-icon" size={40} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                  <h3>{t.why.feature1.title}</h3>
                  <p>{t.why.feature1.desc}</p>
                </div>
                <div className="feature-card">
                  <MapPin className="feature-icon" size={40} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                  <h3>{t.why.feature2.title}</h3>
                  <p>{t.why.feature2.desc}</p>
                </div>
                <div className="feature-card">
                  <Heart className="feature-icon" size={40} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                  <h3>{t.why.feature4.title}</h3>
                  <p>{t.why.feature4.desc}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Our Apartments Section */}
          <section style={{background: 'var(--color-beige)'}}>
            <div className="container">
              <h2 className="section-title">{t.ourApartments.title}</h2>
              <p style={{textAlign: 'center', fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto 2rem', color: 'var(--color-text)'}}>{t.ourApartments.subtitle}</p>
              <div className="amenities-list">
                {t.ourApartments.amenities.map((amenity, index) => (
                  <div key={index} className="amenity-item">{amenity}</div>
                ))}
              </div>
              <div style={{textAlign: 'center', marginTop: '3rem'}}>
                <button className="cta-badge" onClick={() => navigateTo('apartments')}>
                  {language === 'es' ? 'Ver todos los apartamentos' : 'View all apartments'}
                </button>
              </div>
            </div>
          </section>

          {/* Triana Section */}
          <section className="triana-section">
            <div className="container">
              <div className="triana-content">
                <h2 className="section-title">{t.triana.title}</h2>
                <p>{t.triana.description}</p>
              </div>
            </div>
          </section>

          {/* Book Now Section */}
          <section style={{background: 'var(--color-white)'}}>
            <div className="container" style={{textAlign: 'center'}}>
              <h2 className="section-title">{t.bookNow.title}</h2>
              <p style={{fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto 2.5rem', color: 'var(--color-dark-brown)'}}>{t.bookNow.description}</p>
              <button className="cta-badge" onClick={() => navigateTo('contact')}>
                {t.nav.book}
              </button>
            </div>
          </section>
        </>
      )}

      {/* APARTMENTS PAGE */}
      {currentPage === 'apartments' && (
        <>
          <div className="page-header">
            <h1>{t.apartmentsPage.title}</h1>
            <p>{t.apartmentsPage.subtitle}</p>
          </div>

          <section style={{background: 'var(--color-cream)'}}>
            <div className="container">
              <div className="apartments-intro">
                <p>{t.apartmentsPage.intro}</p>
              </div>

              <h2 style={{fontFamily: 'var(--font-display)', fontSize: '2.2rem', color: 'var(--color-brown)', textAlign: 'center', marginBottom: '3rem'}}>{t.apartmentsPage.typesTitle}</h2>
              
              <div className="apartment-types">
                <div className="apartment-card">
                  <div className="apartment-image" role="img" aria-label={language === 'es' ? 'Estudio acogedor' : 'Cozy studio'} style={{backgroundImage: 'url(https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?q=80&w=1080&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)'}}></div>
                  <div className="apartment-content">
                    <Home size={32} strokeWidth={1.5} style={{color: 'var(--color-gold)', marginBottom: '1rem'}} aria-hidden="true" />
                    <h3>{t.apartmentsPage.studio.title}</h3>
                    <p>{t.apartmentsPage.studio.desc}</p>
                  </div>
                </div>
                <div className="apartment-card">
                  <div className="apartment-image" role="img" aria-label={language === 'es' ? 'Apartamento de un dormitorio' : 'One bedroom apartment'} style={{backgroundImage: 'url(https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=600)'}}></div>
                  <div className="apartment-content">
                    <BedDouble size={32} strokeWidth={1.5} style={{color: 'var(--color-gold)', marginBottom: '1rem'}} aria-hidden="true" />
                    <h3>{t.apartmentsPage.oneBed.title}</h3>
                    <p>{t.apartmentsPage.oneBed.desc}</p>
                  </div>
                </div>
                <div className="apartment-card">
                  <div className="apartment-image" role="img" aria-label={language === 'es' ? 'Apartamento familiar' : 'Family apartment'} style={{backgroundImage: 'url(https://images.unsplash.com/photo-1615873968403-89e068629265?w=600)'}}></div>
                  <div className="apartment-content">
                    <Users size={32} strokeWidth={1.5} style={{color: 'var(--color-gold)', marginBottom: '1rem'}} aria-hidden="true" />
                    <h3>{t.apartmentsPage.family.title}</h3>
                    <p>{t.apartmentsPage.family.desc}</p>
                  </div>
                </div>
              </div>

              <div className="includes-section">
                <h2 style={{fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--color-brown)', textAlign: 'center', marginBottom: '2rem'}}>{t.apartmentsPage.includesTitle}</h2>
                <div className="includes-grid">
                  {t.apartmentsPage.includes.map((item, index) => (
                    <div key={index} className="include-item">{item}</div>
                  ))}
                </div>
              </div>

              <div className="info-cards">
                <div className="info-card">
                  <h2>{t.apartmentsPage.design.title}</h2>
                  <p>{t.apartmentsPage.design.desc}</p>
                </div>
                <div className="info-card">
                  <h2>{t.apartmentsPage.location.title}</h2>
                  <p>{t.apartmentsPage.location.desc}</p>
                </div>
              </div>

              <div style={{textAlign: 'center', marginTop: '4rem', padding: '4rem 2rem', background: 'var(--color-beige)', borderRadius: '12px'}}>
                <h2 style={{fontFamily: 'var(--font-display)', fontSize: '2.2rem', color: 'var(--color-dark-brown)', marginBottom: '1.5rem'}}>{t.apartmentsPage.bookCta.title}</h2>
                <p style={{fontSize: '1.2rem', color: 'var(--color-text)', marginBottom: '1rem'}}>{t.apartmentsPage.bookCta.desc}</p>
                <p style={{fontSize: '1.3rem', color: 'var(--color-dark-brown)', fontStyle: 'italic', fontWeight: 600, marginBottom: '2rem'}}>{t.apartmentsPage.bookCta.cta}</p>
                <button className="cta-badge" onClick={() => navigateTo('contact')}>
                  {t.nav.book}
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* CONTACT PAGE */}
      {currentPage === 'contact' && (
        <>
          <div className="page-header">
            <h1>{t.contactPage.title}</h1>
            <p>{t.contactPage.subtitle}</p>
          </div>

          <section style={{background: 'var(--color-white)'}}>
            <div className="container">
              <div className="contact-intro">
                <p>{t.contactPage.intro}</p>
              </div>

              <div className="contact-form-section">
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem'}}>
                  <Mail size={28} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                  <h2>{t.contactPage.formTitle}</h2>
                </div>
                <p className="form-subtitle">{t.contactPage.formSubtitle}</p>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label htmlFor="contact-name">
                      {t.contactPage.form.name} <span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      aria-required="true"
                      autoComplete="name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contact-email">
                      {t.contactPage.form.email} <span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      aria-required="true"
                      autoComplete="email"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contact-phone">{t.contactPage.form.phone}</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contact-dates">{t.contactPage.form.dates}</label>
                    <input
                      id="contact-dates"
                      type="text"
                      name="dates"
                      value={formData.dates}
                      onChange={handleFormChange}
                      placeholder={language === 'es' ? 'ej: 15-20 Marzo 2026' : 'e.g. 15-20 March 2026'}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contact-message">
                      {t.contactPage.form.message} <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      required
                      aria-required="true"
                    ></textarea>
                  </div>
                  <button type="submit" className="submit-button">{t.contactPage.form.submit}</button>
                  <p className="response-time">{t.contactPage.response}</p>
                </form>
              </div>

              <div className="contact-info-grid">
                <div className="contact-info-card">
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem'}}>
                    <MapPin size={28} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    <h2>{t.contactPage.locationTitle}</h2>
                  </div>
                  <p>{t.contactPage.locationDesc}</p>
                </div>
                <div className="contact-info-card">
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem'}}>
                    <Phone size={28} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    <h2>{t.contactPage.channelsTitle}</h2>
                  </div>
                  <div className="contact-detail">
                    <Mail size={18} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    {t.contactPage.channels.email}
                  </div>
                  <div className="contact-detail">
                    <Phone size={18} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    {t.contactPage.channels.phone}
                  </div>
                  <div className="contact-detail">
                    <Clock size={18} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    {t.contactPage.channels.hours}
                  </div>
                </div>
                <div className="contact-info-card">
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem'}}>
                    <MessageCircle size={28} strokeWidth={1.5} style={{color: 'var(--color-gold)'}} aria-hidden="true" />
                    <h2>{t.contactPage.personalTitle}</h2>
                  </div>
                  <p>{t.contactPage.personalDesc}</p>
                </div>
              </div>

              <div className="final-cta-section">
                <h2>{t.contactPage.finalCta}</h2>
                <button className="cta-badge" onClick={() => navigateTo('home')}>
                  {t.nav.home}
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      </main>

      {/* Footer */}
      <footer aria-label={language === 'es' ? 'Pie de página' : 'Footer'}>
        <div className="footer-content">
          <div className="footer-section">
            <h3>TRIANA MÍA</h3>
            <p>{t.footer.aboutText}</p>
          </div>
          <div className="footer-section">
            <h3>{t.footer.links}</h3>
            <button className="footer-link" onClick={() => navigateTo('home')}>{t.nav.home}</button>
            <button className="footer-link" onClick={() => navigateTo('apartments')}>{t.nav.apartments}</button>
            <button className="footer-link" onClick={() => navigateTo('contact')}>{t.nav.contact}</button>
          </div>
          <div className="footer-section">
            <h3>{t.footer.contact}</h3>
            <p>info@trianamiaapartments.com</p>
            <p>+34 600 000 000</p>
            <p>Triana, Sevilla, España</p>
          </div>
        </div>
        
        <div className="footer-funding">
          <div className="footer-funding-text">
            {t.footer.funding}
          </div>
          <div className="footer-funding-logos">
            <img src="/Logo-PRTR.png" alt={language === 'es' ? 'Financiado por la Unión Europea — NextGenerationEU' : 'Funded by the European Union — NextGenerationEU'} />
            <img src="/Logo-PRTR-2.png" alt={language === 'es' ? 'Plan de Recuperación, Transformación y Resiliencia' : 'Recovery, Transformation and Resilience Plan'} />
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2026 Triana Mía Apartments. {t.footer.rights}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;